import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Save, X, Search, Building, Users, Link } from 'lucide-react'
import { tenantAPI } from '../services/api'

const TenantManagement = ({ isAdmin = false }) => {
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingTenant, setEditingTenant] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    isActive: true,
    max_users: 100,
    features: [],
    change_trigger_url: ''
  })

  const [errors, setErrors] = useState({})



  // Load tenants
  useEffect(() => {
    fetchTenants()
  }, [])

  const fetchTenants = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await tenantAPI.getAll()
      
      if (response.data.success) {
        setTenants(response.data.data || [])
      } else {
        setError(response.data.message || 'Failed to fetch tenants')
      }
    } catch (error) {
      console.error('Error fetching tenants:', error)
      setError('Failed to load tenants')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      let response
      
      if (editingTenant) {
        response = await tenantAPI.update(editingTenant.id, formData)
      } else {
        response = await tenantAPI.create(formData)
      }
      
      if (response.data.success) {
        await fetchTenants()
        resetForm()
        setShowCreateForm(false)
        setEditingTenant(null)
      } else {
        setError(response.data.message || 'Failed to save tenant')
      }
    } catch (error) {
      console.error('Error saving tenant:', error)
      setError('Failed to save tenant')
    } finally {
      setLoading(false)
    }
  }

  const handleSoftDelete = async (tenantId) => {
    if (!window.confirm('Are you sure you want to soft delete this tenant? This action can be reversed.')) {
      return
    }
    
    try {
      setLoading(true)
      const response = await tenantAPI.softDelete(tenantId)
      
      if (response.data.success) {
        await fetchTenants()
      } else {
        setError(response.data.message || 'Failed to delete tenant')
      }
    } catch (error) {
      console.error('Error deleting tenant:', error)
      setError('Failed to delete tenant')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (tenant) => {
    setEditingTenant(tenant)
    setFormData({
      name: tenant.name || '',
      description: tenant.description || '',
      domain: tenant.domain || '',
      contact_email: tenant.contact_email || '',
      contact_phone: tenant.contact_phone || '',
      address: tenant.address || '',
      isActive: tenant.isActive !== false,
      max_users: tenant.max_users || 100,
      features: tenant.features || [],
      change_trigger_url: tenant.change_trigger_url || ''
    })
    setShowCreateForm(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      domain: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      isActive: true,
      max_users: 100,
      features: [],
      change_trigger_url: ''
    })
    setErrors({})
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Tenant name is required'
    }

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required'
    }

    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Contact email is invalid'
    }

    if (formData.max_users < 1) {
      newErrors.max_users = 'Max users must be at least 1'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const filteredTenants = tenants.filter(tenant =>
    tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const availableFeatures = [
    { id: 'templates', name: 'Templates' },
    { id: 'categories', name: 'Categories' },
    { id: 'users', name: 'User Management' },
    { id: 'analytics', name: 'Analytics' },
    { id: 'api', name: 'API Access' },
    { id: 'export', name: 'Export Features' }
  ]

  const handleFeatureToggle = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }))
  }

  const handleUpdateChangeTriggerUrl = async (tenantId) => {
    const newUrl = window.prompt('Enter new change trigger URL:', '')
    if (newUrl === null) return // User cancelled

    try {
      setLoading(true)
      const response = await tenantAPI.updateChangeTriggerUrl(tenantId, { change_trigger_url: newUrl })
      
      if (response.data.success) {
        await fetchTenants() // Refresh the list
      } else {
        setError(response.data.message || 'Failed to update change trigger URL')
      }
    } catch (error) {
      console.error('Error updating change trigger URL:', error)
      setError('Failed to update change trigger URL')
    } finally {
      setLoading(false)
    }
  }

  if (loading && tenants.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <Building className="w-8 h-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
        </div>
        {isAdmin && (
          <button
            onClick={() => {
              resetForm()
              setEditingTenant(null)
              setShowCreateForm(true)
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Tenant</span>
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">
            {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tenant name"
                />
                {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain *
                </label>
                <input
                  type="text"
                  required
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="tenant.example.com"
                />
                {errors.domain && <p className="text-sm text-red-600 mt-1">{errors.domain}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder="Tenant description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="admin@tenant.com"
                />
                {errors.contact_email && <p className="text-sm text-red-600 mt-1">{errors.contact_email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1-555-0000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="2"
                placeholder="Full address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Change Trigger URL
              </label>
              <input
                type="url"
                value={formData.change_trigger_url}
                onChange={(e) => setFormData({ ...formData, change_trigger_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://api.example.com/webhook/tenant-changes"
              />
              <p className="text-xs text-gray-500 mt-1">
                URL to be called when tenant data changes (optional)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Users
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_users}
                  onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.max_users && <p className="text-sm text-red-600 mt-1">{errors.max_users}</p>}
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Features
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {availableFeatures.map((feature) => (
                  <label key={feature.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.features.includes(feature.id)}
                      onChange={() => handleFeatureToggle(feature.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{feature.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{editingTenant ? 'Update' : 'Create'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setEditingTenant(null)
                  resetForm()
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tenants List */}
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Tenants ({filteredTenants.length})</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredTenants.length > 0 ? (
            filteredTenants.map((tenant) => (
              <div key={tenant.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building className="w-6 h-6 text-blue-600" />
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">{tenant.name}</h4>
                      <p className="text-sm text-gray-500">{tenant.domain}</p>
                      <p className="text-sm text-gray-500">{tenant.contact_email}</p>
                      {tenant.change_trigger_url && (
                        <p className="text-sm text-blue-600">
                          🔗 {tenant.change_trigger_url}
                        </p>
                      )}
                      {tenant.description && (
                        <p className="text-sm text-gray-600 mt-1">{tenant.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Max: {tenant.max_users}</span>
                      </div>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                        tenant.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {tenant.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleUpdateChangeTriggerUrl(tenant.id)}
                            className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                            title="Update Change Trigger URL"
                          >
                            <Link className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleEdit(tenant)}
                            className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"
                            title="Edit Tenant"
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleSoftDelete(tenant.id)}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                            title="Delete Tenant"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {tenant.features && tenant.features.length > 0 && (
                  <div className="mt-3 ml-16">
                    <div className="flex flex-wrap gap-1">
                      {tenant.features.map((feature) => (
                        <span
                          key={feature}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                        >
                          {availableFeatures.find(f => f.id === feature)?.name || feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="px-6 py-12 text-center">
              <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tenants found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'No tenants match your search.' : 'Get started by creating your first tenant.'}
              </p>
              {!searchTerm && isAdmin && (
                <button
                  onClick={() => {
                    resetForm()
                    setEditingTenant(null)
                    setShowCreateForm(true)
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Tenant
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TenantManagement
